import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, MessageSquare } from "lucide-react";

export default function ClientContact() {
  const { user } = useAuth();
  const { t, date } = useLang();
  const [threads, setThreads] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(() => {
    api.get("/contact").then((r) => setThreads(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.post("/contact/read-all").then(() => window.dispatchEvent(new Event("contact-updated"))).catch(() => {});
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return toast.error(t("help.need_fields"));
    setSending(true);
    try {
      await api.post("/contact", { subject: subject.trim(), message: message.trim() });
      toast.success(t("help.sent"));
      setSubject(""); setMessage(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const reply = async (thread) => {
    const body = (drafts[thread.id] || "").trim();
    if (!body) return;
    try {
      await api.post(`/contact/${thread.id}/message`, { body });
      setDrafts((d) => ({ ...d, [thread.id]: "" }));
      window.dispatchEvent(new Event("contact-updated"));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="ch-fade-up space-y-8">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("help.eyebrow")}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("help.title")}</h1>
        <p className="mt-2 text-sm text-neutral-500">{t("help.subtitle")}</p>
      </header>

      <form onSubmit={send} className="space-y-4 rounded-sm border border-neutral-200 p-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>{t("help.name")}</Label><Input value={`${user?.prenom || ""} ${user?.nom || ""}`.trim()} disabled className="rounded-sm bg-neutral-50" /></div>
          <div className="space-y-1.5"><Label>{t("help.subject")}</Label><Input data-testid="contact-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("help.subject_ph")} className="rounded-sm" /></div>
        </div>
        <div className="space-y-1.5"><Label>{t("help.message")}</Label><Textarea data-testid="contact-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder={t("help.message_ph")} className="rounded-sm" /></div>
        <div className="flex justify-end">
          <Button type="submit" data-testid="contact-send-button" disabled={sending} className="rounded-sm bg-black hover:bg-neutral-800">
            <Send className="mr-2 h-4 w-4" /> {t("help.send")}
          </Button>
        </div>
      </form>

      <div className="space-y-4">
        <h2 className="font-display text-lg font-bold">{t("help.my_messages")}</h2>
        {threads.length === 0 && (
          <div className="flex flex-col items-center gap-3 rounded-sm border border-dashed border-neutral-200 py-12 text-neutral-400">
            <MessageSquare className="h-7 w-7" strokeWidth={1.5} /><p className="text-sm">{t("help.no_messages")}</p>
          </div>
        )}
        {threads.map((thread) => (
          <div key={thread.id} data-testid={`client-thread-${thread.id}`} className="rounded-sm border border-neutral-200 p-5">
            <div className="flex items-center justify-between">
              <p className="font-display font-bold">{thread.subject}</p>
              <span className="text-xs text-neutral-400">{date(thread.created_at)}</span>
            </div>
            <div className="mt-3 space-y-2">
              {thread.messages.map((m, i) => (
                <div key={i} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-sm px-4 py-2 text-sm ${m.sender === "client" ? "bg-black text-white" : "bg-neutral-100 text-neutral-800"}`}>
                    <p className="text-[10px] uppercase tracking-widest opacity-60">{m.sender === "client" ? t("help.you") : "CH CREATIVE"} · {date(m.created_at)}</p>
                    <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Input data-testid={`client-reply-input-${thread.id}`} value={drafts[thread.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [thread.id]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") reply(thread); }} placeholder={t("help.reply_ph")} className="rounded-sm" />
              <Button data-testid={`client-reply-send-${thread.id}`} onClick={() => reply(thread)} className="shrink-0 gap-2 rounded-sm bg-black hover:bg-neutral-800">
                <Send className="h-4 w-4" /> {t("help.reply_here")}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
